import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
} from "@mui/material";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import {
  buildMetadataWorkbook,
  METADATA_FILE_NAME,
} from "pages/GenerateScormMetadata/buildMetadataSheet";
import { useState } from "react";
import { flushSync } from "react-dom";
import { useIntl } from "react-intl";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import * as XLSX from "xlsx";

const GenerateScormMetadata = () => {
  const { formatMessage: t } = useIntl();
  const { scormMetadataService } = useGlobalContext();

  useTitle(t({ id: "generateScormMetadataPageTitle" }));

  const [allProducts, setAllProducts] = useState(true);
  const [webinars, setWebinars] = useState(false);
  const [selfStudyDownloads, setSelfStudyDownloads] = useState(false);
  const [selfStudyWebcasts, setSelfStudyWebcasts] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [snackbarMessage, setSnackbarMessage] =
    useState<SnackbarMessageProps | null>(null);

  const anyProductTypeSelected =
    webinars || selfStudyDownloads || selfStudyWebcasts;
  const canGenerate = (allProducts || anyProductTypeSelected) && !generating;

  const generate = async () => {
    setGenerating(true);
    setShowProgress(true);

    try {
      const response = await scormMetadataService.getScormMetadata(
        allProducts || webinars,
        allProducts || selfStudyDownloads,
        allProducts || selfStudyWebcasts,
      );
      const rows = response?.data ?? [];

      // Rendered synchronously so the dialog is gone before the browser starts the download.
      flushSync(() => setShowProgress(false));

      if (rows.length === 0) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "generateScormMetadataEmpty" }),
            Severity.Warning,
          ),
        );
        return;
      }

      XLSX.writeFile(buildMetadataWorkbook(rows), METADATA_FILE_NAME);

      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "generateScormMetadataDownloaded" }, { count: rows.length }),
          Severity.Success,
        ),
      );
    } catch (error: any) {
      setShowProgress(false);
      setSnackbarMessage(
        new SnackbarMessageProps(
          error?.data?.title ?? translateError(error, t),
          Severity.Error,
        ),
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Container>
      <Box my={4}>
        <Typography variant="h5" component="h1" gutterBottom>
          {t({ id: "generateScormMetadataPageTitle" })}
        </Typography>

        <Typography variant="body1" gutterBottom>
          {t({ id: "generateScormMetadataDescription" })}
        </Typography>

        <FormGroup sx={{ my: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={allProducts}
                onChange={(event) => setAllProducts(event.target.checked)}
              />
            }
            label={t({ id: "generateScormMetadataAllProducts" })}
            disabled={anyProductTypeSelected || generating}
          />
          <FormControlLabel
            control={
              <Switch
                checked={webinars}
                onChange={(event) => setWebinars(event.target.checked)}
              />
            }
            label={t({ id: "generateScormMetadataWebinars" })}
            disabled={allProducts || generating}
          />
          <FormControlLabel
            control={
              <Switch
                checked={selfStudyDownloads}
                onChange={(event) =>
                  setSelfStudyDownloads(event.target.checked)
                }
              />
            }
            label={t({ id: "generateScormMetadataSelfStudyDownload" })}
            disabled={allProducts || generating}
          />
          <FormControlLabel
            control={
              <Switch
                checked={selfStudyWebcasts}
                onChange={(event) => setSelfStudyWebcasts(event.target.checked)}
              />
            }
            label={t({ id: "generateScormMetadataSelfStudyWebcasts" })}
            disabled={allProducts || generating}
          />
        </FormGroup>

        <Button variant="contained" disabled={!canGenerate} onClick={generate}>
          {t({ id: "generateScormMetadataGenerate" })}
        </Button>

      </Box>

      <Dialog open={showProgress} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogContent dividers>
          <Loader immediate margin={16} />
          <Typography variant="body1" align="center">
            {t({ id: "generateScormMetadataInProgress" })}
          </Typography>
        </DialogContent>
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default GenerateScormMetadata;
